import type { CookieOptions } from 'nuxt/app'
import type { EffectScope } from 'vue'
import type {PersistentState, PersistentStateStorage} from '~/types/persistent'
import {StorageType} from '~/types/persistent'
import {usePersistentStore} from '~/stores/persistent'
import { isServer } from '~/utils/env'
import { serialize } from 'cookie-es'
import { getRequestHost, getStampinupDomainFromHost } from '~/utils/locale'
import { parseQueryValue, SESSION_GATED_PARAMS } from '~/utils/persistent-query'

type PersistentValue = PersistentState[keyof PersistentState]
type WritableCookieOptions = CookieOptions<PersistentValue> & { readonly?: false }

// useCookie encodes inside a watcher, after the $onAction subscriber returns, so an
// unencodable value would escape the subscriber's try/catch and throw later,
// untracked. Running the same encoding first keeps that failure in the catch.
// Mirrors useCookie's CookieDefaults.encode (string: encodeURIComponent(value);
// anything else: encodeURIComponent(JSON.stringify(value))) -- keep in step with it.
// The string branch matters: encodeURIComponent('\ud800') throws a URIError while
// JSON.stringify('\ud800') does not. null and undefined delete the cookie and never
// reach the encoder. AB#64458
const assertEncodable = (value: PersistentValue) => {
  if (value == null) { return }
  if (typeof value === 'string') {
    encodeURIComponent(value)
  } else {
    encodeURIComponent(JSON.stringify(value))
  }
}

const PERSISTENT_STORAGE: PersistentStateStorage = {
  sharedCartId: StorageType.Cookie,
  isDemo: StorageType.Cookie,
  viewAsDate: StorageType.Cookie,
  demoViewAsDate: StorageType.Cookie,
  units: StorageType.Cookie,
  pwa: StorageType.Cookie,
  noTranslate: StorageType.Cookie,
  pwaPrompt: StorageType.Cookie,
  enabledFeature: StorageType.Cookie,
  hybridRoute: StorageType.Cookie,
  expirationDialogTime: StorageType.Cookie,
  highlightDemo: StorageType.Cookie,
  demoReferral: StorageType.Cookie,
  utm: StorageType.Cookie
}

// Never taken from the URL by this plugin. demoReferral: a query override
// would let any link fabricate the Clarity referral segment. expirationDialogTime
// and isDemo come from sign-in: a link could open the session-expired dialog at
// once, re-mint the session, set the auth cookie expiry or pose as a
// demonstrator session. sharedCartId is adopted on the cart route only, by
// init-auth on the server and the cart page on the client, in the shared-cart
// flow the backend checks. AB#64760
const APP_OWNED: Array<keyof PersistentState> = ['demoReferral', 'expirationDialogTime', 'isDemo', 'sharedCartId']

// Taken from the URL by init-auth alone, once the session is known; each key's
// gate is in SESSION_GATED_PARAMS. AB#64453 AB#64760 AB#65034
const SESSION_GATED: Array<keyof PersistentState> = SESSION_GATED_PARAMS.map(param => param.key)

export default defineNuxtPlugin({
  name: 'persistent',
  async setup(nuxtApp) {
    const route = useRoute()
    const persistentStore = usePersistentStore()

    // One live cookie ref per key. Every useCookie call arms its own expiry
    // timer, deep watcher and cookieStore/BroadcastChannel listener, all bound to
    // that call's `expires`; a superseded ref left alive deletes the renewed
    // cookie when its earlier expiry fires. Stopping its scope disposes all three.
    const cookieOwners = new Map<string, EffectScope>()

    // Nuxt2 scopes every cookie to the market domain (its plugins/auth.ts), and
    // both apps serve the same host, so these share that scope: a host-only
    // write is a second cookie under the same name. Read from the host itself
    // on the server, resolved the same way the locale is (getRequestHost); off
    // a market host (localhost) the browser would drop a Domain cookie, so those
    // stay host-only.
    const marketDomain = () => {
      const headers = useRequestEvent()?.node.req.headers
      const host = headers ? getRequestHost(headers) : location.hostname
      return (host && getStampinupDomainFromHost(host)) || undefined
    }

    // Nuxt4 wrote these host-only before it took up the market-domain scope. A
    // copy left from then is a second cookie that can be the one read back, so
    // each domain-scoped write drops it.
    // TODO: Remove, with the forced write in setCookie, once those have aged out.
    const clearHostOnlyCookie = (name: string, opts: WritableCookieOptions) => {
      const cleared = serialize(name, '', { ...opts, path: opts.path ?? '/', maxAge: -1, expires: new Date(0) })
      const event = useRequestEvent()
      if (event) {
        event.node.res.appendHeader('set-cookie', cleared)
      } else {
        document.cookie = cleared
      }
    }

    // Nuxt owns JSON encoding/decoding; null deletes instead of storing "null".
    const setCookie = (name: string, value: PersistentValue, opts: WritableCookieOptions) => {
      nuxtApp.runWithContext(() => {
        const domain = marketDomain()
        cookieOwners.get(name)?.stop()
        const owner = effectScope(true)
        cookieOwners.set(name, owner)
        owner.run(() => {
          // useCookie skips a write whose value matches what it read, and what
          // it read may be the host-only copy dropped below. `refresh` and the
          // trigger make the domain write unconditional, so that drop can never
          // be the only thing that happens.
          const cookie = useCookie<PersistentValue>(name, { ...opts, domain, refresh: !!domain })
          cookie.value = value
          if (domain) {
            triggerRef(cookie)
          }
        })
        if (domain) {
          clearHostOnlyCookie(name, opts)
        }
      })
    }
    const setLocalStorage = (name: string, value: PersistentValue) => {
      const storageVal = typeof value === 'string' ? value : JSON.stringify(value)
      localStorage.setItem(name, storageVal)
    }
    const getCookie = async (name: string) => {
      const val = await nuxtApp.runWithContext(() => {
        const cookie = useCookie(name)
        return cookie.value
      })
      return val
    }
    const persistValue = (key: keyof PersistentState, value: PersistentValue, expire?: Date) => {
      const cookieOpts: WritableCookieOptions = {secure: true, httpOnly: false}
      if (expire) {
        cookieOpts.expires = expire
      }
      if (PERSISTENT_STORAGE[key] === StorageType.Cookie) {
        setCookie(key, value, cookieOpts)
      }
      if (import.meta.client && PERSISTENT_STORAGE[key] === StorageType.LocalStorage) {
        setLocalStorage(key, value)
      }
    }

    // we dont need to duplicate set cookie on client on initial hit since they will be set on ssr already
    if (isServer) {
      const keys = Object.keys(persistentStore.persistentState) as Array<keyof PersistentState>
      for (const key of keys) {
        if (route.query[key] && !APP_OWNED.includes(key) && !SESSION_GATED.includes(key)) {
          const value = parseQueryValue(route.query[key])
          persistValue(key, value as PersistentValue)
          persistentStore.updateValue(key, value as string)
        } else {
          if (PERSISTENT_STORAGE[key] === StorageType.Cookie) {
            const value = await getCookie(key)
            if (value != null) {
              persistentStore.updateValue(key, value)
            }
          }

        }
      }
    }

    // Subscribe after SSR hydration to avoid rewriting cookies we only read.
    persistentStore.$onAction(
      ({
         name, // name of the action
         args
       }) => {
        if (name === 'updateValue') {
          // A failed cookie write must not break the action that triggered it,
          // and must be visible: nuxt2 tracked it as error_persistent_set. The
          // value is encoded first (see assertEncodable), so the write is skipped
          // when it cannot be. AB#64458
          try {
            const value = args[1]
            assertEncodable(value)
            persistValue(args[0], value, args[2])
          } catch (error) {
            nuxtApp.$appInsights?.trackException({
              exception: error as Error,
              properties: { label: 'error_persistent_set', key: args[0] }
            })
          }
        }
      }
    )
  }
})
